import React, { useState } from 'react';
import SidePanel from '@ajat/design-system/SidePanel';
import Button from '@ajat/design-system/Button';
import DatePicker from '@ajat/design-system/DatePicker';
import Input from '@ajat/design-system/Input';
import { ComponentPage, DemoCard, DemoRow } from '../../shared';

export const meta = {
  component: SidePanel,
  componentName: 'SidePanel',
  title: 'Side Panel',
  group: 'Overlays',
  importPath: '@ajat/design-system/SidePanel',
  description:
    'Generic right-side drawer — 880px on desktop, full width on mobile, or a bottom sheet on mobile with `mobileSheet`. Escape and the overlay close it, and body scroll is locked while it is open. It is the shell behind the size guide, the order-details panel, the privacy policy in checkout and the Overall configurator\'s text editor.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', "Fires after the slide-out with the reason: 'close' (X) | 'overlay' | 'escape' | 'swipe'"],
    ['title', 'node', "''", 'Panel heading; on the mobile sheet it is the dialog\'s accessible name only'],
    ['size', "'l' | 'm'", "'l'", 'Desktop width — 880px or 616px; a brand restates the default through --side-panel-width (OVER/LL: 616)'],
    ['floating', 'bool', 'false', 'Desktop: inset from every screen edge and rounded all round (the SideMenu geometry) instead of flush with the right edge; below 1024px the panel is flush as usual (full screen, or the sheet with mobileSheet)'],
    ['mobileSheet', 'bool', 'false', 'Below 1024px, open as a bottom sheet with a swipe handle (swipe down closes) instead of a full-width panel'],
    ['sheetHeader', "'floating' | 'row'", "'floating'", 'On the sheet: only the X over the content, or back · title · X kept as a real row (step flows whose title lives there)'],
    ['pinActions', 'bool', 'true', 'false: with floating, the action row stays in flow right under the content on desktop (no pinned bar); the mobile sheet keeps its sticky bar'],
    ['actions', 'node', '—', 'A row of buttons after the content — in flow on desktop (with floating: a frosted bar pinned to the panel\'s bottom), a sticky full-bleed bar at the sheet\'s bottom on mobile (a submit button reaches a form inside through its `form` attribute)'],
    ['stepKey', 'string | number', '—', 'Step flows: a change re-keys the title and the body (short enter motion) and moves focus to the title'],
    ['focusOnStep', 'bool', 'true', 'false when the new step focuses its own field'],
    ['restoreFocus', 'bool', 'false', 'Give focus back to whatever opened the panel once it has closed'],
    ['bodyRef', 'ref', '—', 'Ref to the scrolling body — scroll a field into view from the consumer instead of querying the panel\'s internals'],
    ['overlay', "'dim' | 'clear'", "'dim'", "'clear' keeps the page behind visible (no dimming) — e.g. a live preview; an outside click still closes"],
    ['closeLabel', 'string', "'Close'", 'Accessible name of the X button'],
    ['onBack', 'func', '—', 'Renders a back icon button (Tertiary/Gray, ArrowLeft) left of the title — for drawers with steps'],
    ['backLabel', 'string', "'Back'", 'Accessible name of the back button'],
    ['className', 'string', "''", 'Extra class on the panel — placement only; the skin is the --side-panel-* tokens (surface, header surface/border, width), set per brand'],
    ['children', 'node', '—', 'Panel body'],
  ],
  usage: [
    {
      title: 'Size guide',
      code: `import SidePanel from '@ajat/design-system/SidePanel';

<SidePanel isOpen={isOpen} onClose={onClose} title={SIZE_GUIDE_TITLE}>
  <SizeGuideContent items={SIZE_GUIDE_ITEMS} />
</SidePanel>`,
    },
    {
      title: 'Forms inside a panel',
      code: `// A DatePicker inside a SidePanel must keep mobileMode="auto".
// Forcing "popup" traps the calendar behind the panel: the popup
// sits at z-index 100, the panel at 1000.
<SidePanel isOpen={open} onClose={close} title="Klasserepræsentant">
  <Input label="Navn" />
  <DatePicker label="Afrejsedato" />
</SidePanel>`,
    },
  ],
};

export default function SidePanelPage() {
  const [simple, setSimple] = useState(false);
  const [form, setForm] = useState(false);
  const [long, setLong] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [withActions, setWithActions] = useState(false);
  const [stepped, setStepped] = useState(false);
  const [step, setStep] = useState(1);
  const [floating, setFloating] = useState(false);
  const [floatingInline, setFloatingInline] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live" hint="Escape, the X or the overlay close it">
        <DemoRow align="center">
          <Button size="secondary" variant="main" onClick={() => setSimple(true)}>Open panel</Button>
          <Button size="secondary" variant="outline" onClick={() => setForm(true)}>Panel with a form</Button>
          <Button size="secondary" variant="outline" onClick={() => setLong(true)}>Long, scrolling body</Button>
          <Button size="secondary" variant="outline" onClick={() => setSheet(true)}>Bottom sheet on mobile</Button>
          <Button size="secondary" variant="outline" onClick={() => setWithActions(true)}>Actions bar + size m</Button>
          <Button size="secondary" variant="outline" onClick={() => { setStep(1); setStepped(true); }}>Step flow</Button>
          <Button size="secondary" variant="outline" onClick={() => setFloating(true)}>Floating</Button>
          <Button size="secondary" variant="outline" onClick={() => setFloatingInline(true)}>Floating, inline actions</Button>
        </DemoRow>

        <SidePanel
          isOpen={withActions}
          onClose={() => setWithActions(false)}
          title="Välj färg"
          size="m"
          mobileSheet
          overlay="clear"
          actions={<Button form="ds-side-panel-demo-form" type="submit" size="secondary" variant="black" fullWidth>Spara</Button>}
        >
          <form id="ds-side-panel-demo-form" onSubmit={(e) => { e.preventDefault(); setWithActions(false); }}>
            <Input label="Namn" />
          </form>
        </SidePanel>

        <SidePanel
          isOpen={stepped}
          onClose={() => setStepped(false)}
          title={step === 1 ? 'Logga in' : 'Kolla din e-post'}
          stepKey={step}
          onBack={step > 1 ? () => setStep(1) : undefined}
          restoreFocus
          mobileSheet
          sheetHeader="row"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-m)' }}>
            <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
              {step === 1 ? 'Step one — the title took focus when the panel opened.' : 'Step two — the title and body re-entered, focus moved to the title; back returns.'}
            </p>
            {step === 1 && <Button variant="main" onClick={() => setStep(2)}>Nästa steg</Button>}
          </div>
        </SidePanel>

        <SidePanel
          isOpen={floating}
          onClose={() => setFloating(false)}
          title="Floating"
          floating
          size="m"
          actions={<Button size="secondary" variant="black" fullWidth onClick={() => setFloating(false)}>Klar</Button>}
        >
          <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
            Inset from every edge and rounded all round — the SideMenu geometry, on the right. With `actions` the row
            is a frosted bar pinned to the panel&apos;s bottom on desktop (the sheet bar on phones).
          </p>
        </SidePanel>

        <SidePanel
          isOpen={floatingInline}
          onClose={() => setFloatingInline(false)}
          title="Floating, inline actions"
          floating
          size="m"
          pinActions={false}
          mobileSheet
          actions={<Button size="secondary" variant="black" fullWidth onClick={() => setFloatingInline(false)}>Spara</Button>}
        >
          <Input label="Text" />
        </SidePanel>

        <SidePanel isOpen={simple} onClose={() => setSimple(false)} title="Order Details">
          <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
            Any content goes here. The panel owns the chrome — heading, close button, scroll lock —
            and nothing else.
          </p>
        </SidePanel>

        <SidePanel isOpen={form} onClose={() => setForm(false)} title="Klasserepræsentant">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-m)' }}>
            <Input label="Navn" />
            <Input label="E-mail" type="email" />
            <DatePicker label="Afrejsedato" />
            <Button variant="main">Gem</Button>
          </div>
        </SidePanel>

        <SidePanel isOpen={long} onClose={() => setLong(false)} title="Personuppgiftspolicy">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-m)' }}>
            {Array.from({ length: 12 }, (_, i) => (
              <p key={i} className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
                <strong className="typo-h5" style={{ display: 'block', color: 'var(--color-gray-10)' }}>
                  {i + 1}. Afsnit
                </strong>
                Body scroll is locked behind the panel while it is open, and restored on close — so the
                page underneath does not move as you scroll this text.
              </p>
            ))}
          </div>
        </SidePanel>

        <SidePanel isOpen={sheet} onClose={() => setSheet(false)} title="Redigera text" mobileSheet overlay="clear" closeLabel="Stäng">
          <p className="typo-body" style={{ color: 'var(--color-gray-7)' }}>
            On desktop this is the regular side panel. Below 1024px it opens from the bottom with a
            swipe handle — drag it, or the content from its top, down to close.
          </p>
        </SidePanel>
      </DemoCard>

      <DemoCard title="Behaviour" surface={false}>
        <ul className="typo-body" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch', paddingLeft: '1.2em', listStyle: 'disc' }}>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>880px on desktop, full width below — or, with <code>mobileSheet</code>, a bottom sheet below 1024px (title hidden, X in the corner, swipe down to close).</li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>Escape, the overlay and the X all fire <code>onClose</code>.</li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>Body scroll is locked while open and restored on close.</li>
          <li>Slides out over <code>--transition-time-drawer</code> on the <code>--transition-function-drawer</code> spring before unmounting.</li>
        </ul>
      </DemoCard>
    </ComponentPage>
  );
}
